// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { useEffect, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { config, features, ossKey } from "@/lib/config";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useScrollDepthTracking } from "@/hooks/analytics";
import { SectionTransition } from "@/lib/motion";

import { Header } from "@/components/landing/Header";
import { CinematicHero } from "@/components/landing-v2/CinematicHero";
import { LandingNav } from "@/components/landing-v2/LandingNav";

const ProofStrip = lazy(() => import("@/components/landing-v2/ProofStrip").then((m) => ({ default: m.ProofStrip })));
const ZeroKnowledgePipeline = lazy(() => import("@/components/landing-v2/ZeroKnowledgePipeline").then((m) => ({ default: m.ZeroKnowledgePipeline })));
const FeatureConstellation = lazy(() => import("@/components/landing-v2/FeatureConstellation").then((m) => ({ default: m.FeatureConstellation })));
const AndroidAppSection = lazy(() => import("@/components/landing/AndroidAppSection").then((m) => ({ default: m.AndroidAppSection })));
const OpenAndYours = lazy(() => import("@/components/landing-v2/OpenAndYours").then((m) => ({ default: m.OpenAndYours })));
const ComparisonArena = lazy(() => import("@/components/landing-v2/ComparisonArena").then((m) => ({ default: m.ComparisonArena })));
const PricingSection = lazy(() => import("@/components/landing/PricingSection").then((m) => ({ default: m.PricingSection })));
const LandingFAQ = lazy(() => import("@/components/landing-v2/LandingFAQ").then((m) => ({ default: m.LandingFAQ })));
const FinalCTA = lazy(() => import("@/components/landing-v2/FinalCTA").then((m) => ({ default: m.FinalCTA })));
const Footer = lazy(() => import("@/components/landing/Footer").then((m) => ({ default: m.Footer })));
const StickyBottomCTA = lazy(() => import("@/components/landing/StickyBottomCTA").then((m) => ({ default: m.StickyBottomCTA })));
const LandingV2StructuredData = lazy(() => import("@/components/landing-v2/LandingV2StructuredData").then((m) => ({ default: m.LandingV2StructuredData })));

const OG_LOCALE_MAP: Record<string, string> = {
  ar: "ar_SA",
  fr: "fr_FR",
  de: "de_DE",
  es: "es_ES",
  en: "en_US",
};

const SUPPORTED_LANGS = ["en", "ar", "fr", "de", "es"];

export const Route = createFileRoute("/")({
  component: LandingPage,
});

function LandingPage() {
  const siteUrl = config.siteUrl;
  const { t, i18n } = useTranslation("landing-v2");
  const { t: tCommon } = useTranslation("common");
  const { initScrollTracking } = useScrollDepthTracking();

  useEffect(() => {
    return initScrollTracking();
  }, [initScrollTracking]);

  const ogLocale = OG_LOCALE_MAP[i18n.language] || "en_US";

  return (
    <>
      <Helmet>
        <title>{t(ossKey("meta.title"))}</title>
        <meta name="description" content={t(ossKey("meta.description"))} />
        <meta name="keywords" content={t(ossKey("meta.keywords"))} />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="author" content="pssmngr" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={t("meta.ogTitle")} />
        <meta property="og:description" content={t(ossKey("meta.ogDescription"))} />
        <meta property="og:site_name" content="pssmngr" />
        <meta property="og:image" content={`${siteUrl}/og-landing.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={ogLocale} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("meta.ogTitle")} />
        <meta name="twitter:description" content={t(ossKey("meta.ogDescription"))} />
        <meta name="twitter:image" content={`${siteUrl}/og-landing.png`} />

        <link rel="canonical" href={siteUrl} />

        {/* Alternate language links */}
        {SUPPORTED_LANGS.map((lang) => (
          <link key={lang} rel="alternate" hrefLang={lang} href={`${siteUrl}?lang=${lang}`} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={siteUrl} />
      </Helmet>

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-background focus:text-foreground"
      >
        {tCommon("a11y.skipToContent", "Skip to main content")}
      </a>

      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <Header />
        <LandingNav />

        <main id="main-content" role="main" className="flex-1 flex flex-col">
          {/* Above-the-fold: eagerly loaded */}
          <CinematicHero />

          {/* Group 1: proof + security pipeline */}
          <Suspense fallback={null}>
            <SectionTransition>
              <ProofStrip />
            </SectionTransition>
            <ZeroKnowledgePipeline />
          </Suspense>

          {/* Group 2: features + Android app + open source + comparison */}
          <Suspense fallback={null}>
            <SectionTransition>
              <FeatureConstellation />
            </SectionTransition>
            <SectionTransition>
              <AndroidAppSection />
            </SectionTransition>
            {features.openSource && (
              <SectionTransition>
                <OpenAndYours />
              </SectionTransition>
            )}
            <SectionTransition>
              <ComparisonArena />
            </SectionTransition>
          </Suspense>

          {/* Group 3: pricing + FAQ + closing CTA */}
          <Suspense fallback={null}>
            {features.payments && (
              <SectionTransition>
                <PricingSection />
              </SectionTransition>
            )}
            <SectionTransition>
              <LandingFAQ />
            </SectionTransition>
            <SectionTransition>
              <FinalCTA />
            </SectionTransition>
          </Suspense>
        </main>

        {/* Footer + Utilities */}
        <Suspense fallback={null}>
          <Footer />
          <StickyBottomCTA />
          <LandingV2StructuredData />
        </Suspense>
      </div>
    </>
  );
}
